// Compra desde foto de factura — reglas de pantalla, sin Vue ni red.
// Experimento: agente_productos/docs/SPEC_COMPRA_DESDE_FACTURA_v0.1.md (fase 2).
// El agente devuelve un borrador; el operador lo revisa y la compra la crea
// esta pantalla con POST /api/purchases/invoices y el token del operador.

import { estadoPrecio, margenSobreCosto, planDePrecios } from './cargaInicial.mjs';
import { avisoCantidad, costoPorUnidad } from './presentacionCompra.mjs';

export const PERMISO_COMPRA_FOTO = 'crear_factura_compra';

/** La pantalla existe sólo con el flag de build y el permiso de compra. */
export function puedeUsarCompraDesdeFoto({ flag, permisos = [] } = {}) {
  return String(flag).toLowerCase() === 'true' && permisos.includes(PERMISO_COMPRA_FOTO);
}

const numero = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};

const NRO_COMPROBANTE = /^\d{3}-\d{3}-\d{7}$/;

/** Borrador del agente → estado editable de la pantalla. */
export function borradorAEdicion(borrador) {
  const proveedor = borrador?.proveedor || {};
  const cabecera = borrador?.cabecera || {};
  return {
    proveedor: {
      proveedor_id: proveedor.estado === 'encontrado' ? proveedor.proveedor_id ?? null : null,
      estado: proveedor.estado || 'sin_dato',
      nro_documento: proveedor.estado === 'encontrado' ? proveedor.documento : (proveedor.documento || ''),
      nombre: proveedor.nombre || null,
      nombre_leido: proveedor.nombre_leido || null,
      direccion: ''
    },
    cabecera: {
      nro_comprobante: cabecera.nro_comprobante || '',
      timbrado: cabecera.timbrado || '',
      fecha_emision: cabecera.fecha_emision || '',
      credito_contado: cabecera.credito_contado === 'CREDITO' ? 'CREDITO' : 'CONTADO'
    },
    // Lo que vino en el borrador: un aviso del agente sobre un campo deja de
    // mostrarse cuando el operador corrige ese campo.
    original: {
      nro_documento: proveedor.documento || '',
      nro_comprobante: cabecera.nro_comprobante || '',
      timbrado: cabecera.timbrado || '',
      fecha_emision: cabecera.fecha_emision || ''
    },
    total_leido: numero(cabecera.total_leido),
    // facturacion-electronica informó que el emisor canceló el DE en SIFEN.
    documento_cancelado: borrador?.consulta_xml?.estado === 'cancelado',
    documento_no_aprobado: borrador?.consulta_xml?.estado === 'sin_protocolo',
    // Redondeo, descuento global: se muestran, no se cargan (no son mercadería).
    ajustes: (borrador?.items || []).filter((item) => item.es_ajuste).map((item) => ({
      descripcion_leida: item.descripcion_leida,
      subtotal: numero(item.subtotal_leido) || 0
    })),
    lineas: (borrador?.items || []).filter((item) => !item.es_ajuste).map((item) => {
      const elegido = item.producto?.estado === 'resuelto' ? item.producto : null;
      return {
        linea: item.linea,
        descripcion_leida: item.descripcion_leida,
        codigo_leido: item.codigo_leido || null,
        gtin: item.gtin || null,
        producto_id: elegido ? elegido.producto_id : null,
        producto_nombre: elegido ? elegido.nombre : null,
        // Caja, bolsa, bulto (presentacionDeCompra): cantidad y precio quedan
        // como en la factura y el API los convierte a unidades.
        presentacion: null,
        estado_producto: item.producto?.estado || 'no_encontrado',
        candidatos: item.producto?.candidatos || [],
        // Pesables: la cantidad real (1,395 kg) sale del importe; el ticket imprime 1,39.
        cantidad: numero(item.cantidad_calculada) ?? numero(item.cantidad),
        cantidad_impresa: numero(item.cantidad_calculada) !== null ? numero(item.cantidad) : null,
        precio_unitario: numero(item.precio_unitario),
        precio_bruto: numero(item.precio_bruto),
        iva_leido: item.iva_leido ?? null,
        advertencias: item.advertencias || [],
        original: {
          producto_id: elegido ? elegido.producto_id : null,
          cantidad: numero(item.cantidad_calculada) ?? numero(item.cantidad),
          precio_unitario: numero(item.precio_unitario)
        },
        // Detalle del producto del catálogo y su precio de venta: se cargan al
        // elegir el producto (como en Facturas Compras y la carga inicial).
        detalle: null,
        precio: null,
        precio_venta_nuevo: '',
        fecha_vencimiento: ''
      };
    })
  };
}

/** Avisos del agente sobre la cabecera y las líneas, los bloqueantes primero. */
export function avisosDelAgente(borrador) {
  const todos = [
    ...(borrador?.advertencias || []),
    ...(borrador?.items || []).flatMap((i) => i.advertencias || [])
  ];
  return [...todos.filter((a) => a.bloqueante), ...todos.filter((a) => !a.bloqueante)];
}

export function subtotalLinea(linea) {
  const c = numero(linea.cantidad);
  const p = numero(linea.precio_unitario);
  return c && p ? Math.round(c * p) : 0;
}

export function totalEdicion(edicion) {
  return (edicion?.lineas || []).reduce((acc, l) => acc + subtotalLinea(l), 0);
}

/** Diferencia contra el total impreso; null si la factura no lo traía. */
export function totalAjustes(edicion) {
  return (edicion?.ajustes || []).reduce((acc, a) => acc + a.subtotal, 0);
}

/** Diferencia contra el total impreso (con los ajustes); null si no lo traía. */
export function diferenciaConTotalLeido(edicion) {
  if (edicion?.total_leido === null || edicion?.total_leido === undefined) return null;
  return totalEdicion(edicion) + totalAjustes(edicion) - edicion.total_leido;
}

/**
 * Lo que falta para poder confirmar, con el campo al que corresponde: la
 * pantalla lo muestra debajo de ese campo, en rojo. `resumen` es el texto de
 * la lista «Falta para confirmar». `linea` es el número de línea del borrador.
 * Lo que el POS valida igual (duplicado, caja abierta) no se repite acá.
 */
export function pendientesPorCampo(edicion) {
  const pendientes = [];
  const agregar = (campo, mensaje, resumen = mensaje, linea = null) => pendientes.push({ campo, linea, mensaje, resumen });
  if (!edicion) return [{ campo: 'general', linea: null, mensaje: 'Sin borrador', resumen: 'Sin borrador' }];
  if (edicion.documento_cancelado) agregar('general', 'El emisor canceló esta factura en SIFEN: no se puede cargar');
  if (edicion.documento_no_aprobado) agregar('general', 'SIFEN no tiene esta factura como aprobada: no se puede cargar');
  if (edicion.proveedor.estado !== 'encontrado') {
    agregar('proveedor', 'Elegí un proveedor registrado: verificá el RUC, buscalo o registralo', 'Elegí un proveedor registrado (RUC)');
  }
  const nro = String(edicion.cabecera.nro_comprobante || '').trim();
  if (!nro) agregar('nro_comprobante', 'Falta el número de factura');
  else if (!NRO_COMPROBANTE.test(nro)) agregar('nro_comprobante', 'El número de factura debe ser 001-001-0000001');
  if (!edicion.cabecera.fecha_emision) agregar('fecha_emision', 'Falta la fecha de emisión');
  if (!edicion.lineas.length) agregar('general', 'La compra no tiene líneas');
  edicion.lineas.forEach((l, i) => {
    const n = i + 1;
    if (!l.producto_id) agregar('producto', 'Elegí el producto: buscalo en el catálogo o registralo', `Línea ${n}: elegí el producto`, l.linea);
    if (!(numero(l.cantidad) > 0)) agregar('cantidad', 'Falta la cantidad', `Línea ${n}: cantidad`, l.linea);
    // RF-12 (H-4): con caja, la cantidad tiene que dar unidades enteras.
    const aviso = avisoCantidad(l.cantidad, l.presentacion);
    if (aviso) agregar('cantidad', aviso, `Línea ${n}: la cantidad no da unidades enteras`, l.linea);
    if (!(numero(l.precio_unitario) > 0)) agregar('precio', 'Falta el precio unitario', `Línea ${n}: precio unitario`, l.linea);
  });
  return pendientes;
}

/** Lo que falta para poder confirmar. Vacío = se puede enviar al POS. */
export function pendientesEdicion(edicion) {
  return pendientesPorCampo(edicion).map((p) => p.resumen);
}

// Campo de la pantalla al que corresponde cada aviso del agente. Lo que no
// está acá se muestra arriba, como aviso general.
const CAMPO_DE_AVISO = {
  proveedor_no_encontrado: 'proveedor',
  proveedor_nombre_distinto: 'proveedor',
  ruc_dv_invalido: 'proveedor',
  emisor_igual_receptor: 'proveedor',
  factura_duplicada: 'nro_comprobante',
  numero_ya_usado: 'nro_comprobante',
  nro_comprobante_formato: 'nro_comprobante',
  timbrado_faltante: 'timbrado',
  timbrado_invalido: 'timbrado',
  fecha_sospechosa: 'fecha_emision',
  producto_no_encontrado: 'producto',
  producto_ambiguo: 'producto',
  medida_no_indicada: 'producto',
  iva_distinto: 'producto',
  exenta_parece_gravada: 'producto',
  linea_poco_legible: 'producto',
  cantidad_faltante: 'cantidad',
  cantidad_supuesta: 'cantidad',
  posible_presentacion: 'cantidad',
  precio_faltante: 'precio',
  precio_con_descuento: 'precio',
  linea_no_cierra: 'precio',
  total_no_cierra: 'totales',
  liquidacion_no_cierra: 'totales',
  total_desde_letras: 'totales',
  total_en_letras_no_coincide: 'totales',
  qr_no_coincide: 'totales',
  tasas_por_liquidacion: 'totales'
};
const CAMPOS_CABECERA = ['proveedor', 'nro_comprobante', 'timbrado', 'fecha_emision'];

const mismoTexto = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
const mismoNumero = (a, b) => numero(a) === numero(b);

// ¿El aviso sigue valiendo? Deja de valer cuando el operador corrigió el campo.
function avisoVigente(aviso, campo, edicion, linea) {
  const original = edicion.original || {};
  if (campo === 'proveedor') {
    if (!mismoTexto(edicion.proveedor.nro_documento, original.nro_documento)) return false;
    if (['proveedor_no_encontrado', 'ruc_dv_invalido'].includes(aviso.codigo)) return edicion.proveedor.estado !== 'encontrado';
    return true;
  }
  if (CAMPOS_CABECERA.includes(campo)) return mismoTexto(edicion.cabecera[campo], original[campo]);
  if (!linea) return true;
  const o = linea.original || {};
  if (campo === 'producto') {
    if (['producto_no_encontrado', 'producto_ambiguo', 'medida_no_indicada'].includes(aviso.codigo)) return !linea.producto_id;
    return linea.producto_id === o.producto_id;
  }
  if (aviso.codigo === 'linea_no_cierra') return mismoNumero(linea.cantidad, o.cantidad) && mismoNumero(linea.precio_unitario, o.precio_unitario);
  if (campo === 'cantidad') return mismoNumero(linea.cantidad, o.cantidad);
  if (campo === 'precio') return mismoNumero(linea.precio_unitario, o.precio_unitario);
  return true;
}

/**
 * Avisos del agente y faltantes, repartidos por campo para mostrarlos debajo
 * de cada uno: { general, proveedor, nro_comprobante, timbrado, fecha_emision,
 * totales, lineas: { [linea]: { producto, cantidad, precio } } }.
 * Cada aviso: { codigo, mensaje, bloqueante }. Los faltantes son bloqueantes
 * (codigo `falta`) y no se repiten si el agente ya marcó ese campo en rojo.
 */
export function avisosPorCampo(borrador, edicion) {
  const resultado = { general: [], proveedor: [], nro_comprobante: [], timbrado: [], fecha_emision: [], totales: [], lineas: {} };
  if (!edicion) return resultado;
  const lineas = new Map(edicion.lineas.map((l) => [l.linea, l]));
  const ajustes = new Set((borrador?.items || []).filter((i) => i.es_ajuste).map((i) => i.linea));
  const destino = (campo, numeroLinea) => {
    if (numeroLinea === null || numeroLinea === undefined || !['producto', 'cantidad', 'precio'].includes(campo)) return resultado[campo];
    resultado.lineas[numeroLinea] = resultado.lineas[numeroLinea] || { producto: [], cantidad: [], precio: [] };
    return resultado.lineas[numeroLinea][campo];
  };
  for (const aviso of avisosDelAgente(borrador)) {
    const numeroLinea = aviso.linea ?? null;
    let campo = CAMPO_DE_AVISO[aviso.codigo] || (numeroLinea !== null ? 'producto' : 'general');
    let linea = null;
    if (numeroLinea !== null) {
      if (ajustes.has(numeroLinea)) campo = 'totales';
      else if (!lineas.has(numeroLinea)) continue; // línea quitada por el operador
      else linea = lineas.get(numeroLinea);
    }
    if (!avisoVigente(aviso, campo, edicion, linea)) continue;
    const lista = linea ? destino(campo, numeroLinea) : (resultado[campo] || resultado.general);
    lista.push({ codigo: aviso.codigo, mensaje: aviso.mensaje, bloqueante: Boolean(aviso.bloqueante) });
  }
  for (const p of pendientesPorCampo(edicion)) {
    const lista = destino(p.campo, p.linea);
    if (lista.some((a) => a.bloqueante)) continue;
    lista.push({ codigo: 'falta', mensaje: p.mensaje, bloqueante: true });
  }
  return resultado;
}

/** Cuerpo de POST /api/purchases/invoices (mismo formato que Factura.vue). */
export function armarPayloadCompra(edicion) {
  return {
    cabecera: {
      nro_comprobante: String(edicion.cabecera.nro_comprobante).trim(),
      timbrado: String(edicion.cabecera.timbrado || '').trim(),
      nro_nota_remision: null,
      fecha_emision: edicion.cabecera.fecha_emision,
      tipo_moneda: 'PYG',
      credito_contado: edicion.cabecera.credito_contado,
      tipo_documento: 'RUC',
      nro_documento: edicion.proveedor.nro_documento,
      nombre_razon_social: edicion.proveedor.nombre,
      direccion: edicion.proveedor.direccion || ''
    },
    detalles: edicion.lineas.map((l) => ({
      ...(l.presentacion
        ? { presentacion_id: Number(l.presentacion.presentacion_id) }
        : { producto_id: Number(l.producto_id) }),
      cantidad: Number(l.cantidad),
      precio_unitario_bruto: Number(l.precio_unitario),
      descuento: 0,
      fecha_vencimiento: l.fecha_vencimiento || null
    }))
  };
}

/** Liquidación de la factura (según la lectura) para mostrar: sólo filas con dato. */
export function filasLiquidacion(totales) {
  if (!totales) return [];
  const filas = [
    ['Exentas', totales.exentas],
    ['Gravadas 5%', totales.gravada_5],
    ['Gravadas 10%', totales.gravada_10],
    ['IVA 5%', totales.iva_5],
    ['IVA 10%', totales.iva_10],
    ['Total IVA', totales.iva_total],
    ['Total', totales.total]
  ];
  return filas.filter(([, v]) => v !== null && v !== undefined).map(([etiqueta, valor]) => ({ etiqueta, valor }));
}

/** CDC tipeado o pegado: 44 dígitos (con o sin espacios). */
export function cdcValidoEnForma(texto) {
  return String(texto ?? '').replace(/\s/g, '').match(/^\d{44}$/) !== null;
}

// DV módulo 11 (el mismo del RUC).
const dvModulo11 = (numero) => {
  let factor = 2;
  let total = 0;
  for (const d of String(numero).split('').reverse()) {
    total += Number(d) * factor;
    factor = factor < 11 ? factor + 1 : 2;
  }
  const resto = total % 11;
  return resto > 1 ? 11 - resto : 0;
};

/**
 * Lo que el usuario tipea o escanea en «QR o CDC» → CDC validado.
 * Acepta el CDC (con espacios) o la URL del QR del KuDE. Un lector 2D con la
 * distribución de teclado distinta cambia `?`, `=` y `&` de la URL: por eso, si
 * la URL no se puede leer, se busca «Id» seguido de 44 dígitos y, si no, la
 * única tira de 44 dígitos del texto.
 * Devuelve { cdc, qr, emisor, nro, fecha } o { error }.
 */
export function interpretarQrOCdc(texto) {
  const entrada = String(texto ?? '').trim();
  if (!entrada) return { error: null };
  let cdc = null;
  let qr = null;
  const pareceUrl = /https?|ekuatia|consultas|[?&]|Id\W/i.test(entrada);
  if (pareceUrl) {
    try {
      const url = new URL(entrada);
      if (/ekuatia\.set\.gov\.py$/i.test(url.hostname)) {
        cdc = url.searchParams.get('Id');
        if (cdc) qr = entrada;
      }
    } catch (error) {
      // URL deformada por el lector: se rescata el Id abajo.
    }
    if (!cdc) cdc = entrada.match(/Id\W{0,2}(\d{44})(?!\d)/i)?.[1] || null;
    if (!cdc) {
      const tiras = entrada.match(/\d{44,}/g) || [];
      const exactas = tiras.filter((t) => t.length === 44);
      if (exactas.length === 1) cdc = exactas[0];
    }
    if (!cdc) return { error: 'No es el QR de una factura electrónica: no se encontró el CDC (Id) en lo escaneado' };
  } else {
    if (/[^\d\s.-]/.test(entrada)) return { error: 'El CDC sólo lleva números' };
    cdc = entrada.replace(/[\s.-]/g, '');
  }
  if (!/^\d+$/.test(cdc)) return { error: 'El CDC sólo lleva números' };
  if (cdc.length !== 44) return { error: `El CDC tiene que tener 44 números (tiene ${cdc.length})` };
  if (dvModulo11(cdc.slice(0, 43)) !== Number(cdc[43])) {
    return { error: 'El CDC no es válido: el dígito verificador no corresponde. Revisá los números' };
  }
  const fecha = `${cdc.slice(25, 29)}-${cdc.slice(29, 31)}-${cdc.slice(31, 33)}`;
  const fechaReal = !Number.isNaN(Date.parse(`${fecha}T00:00:00Z`))
    && new Date(`${fecha}T00:00:00Z`).toISOString().slice(0, 10) === fecha;
  const tipo = Number(cdc.slice(0, 2));
  if (!(tipo >= 1 && tipo <= 8) || !fechaReal || !['1', '2'].includes(cdc[33])) {
    return { error: 'El CDC no es válido: los números no corresponden a un documento electrónico' };
  }
  const ruc = cdc.slice(2, 10).replace(/^0+/, '');
  return {
    cdc,
    qr,
    emisor: `${ruc}-${cdc[10]}`,
    nro: `${cdc.slice(11, 14)}-${cdc.slice(14, 17)}-${cdc.slice(17, 24)}`,
    fecha
  };
}

/** De dónde salieron los datos del borrador, para decírselo al operador. */
export function origenDeLosDatos(borrador) {
  const fuente = borrador?.lectura?.fuente;
  if (fuente === 'xml_cdc') {
    const como = { qr: 'el QR', cdc_impreso: 'el CDC impreso en' }[borrador?.consulta_xml?.llave] || 'el CDC de';
    return { tipo: 'oficial', texto: `Datos oficiales de SIFEN, consultados por ${como} la factura` };
  }
  if (fuente === 'xml') return { tipo: 'oficial', texto: 'Datos del XML del documento electrónico' };
  if (fuente === 'cdc') return { tipo: 'parcial', texto: 'Sólo la cabecera, sacada del CDC: sacale una foto a la factura para el detalle' };
  if (fuente === 'texto') return { tipo: 'lectura', texto: 'Armado a partir del texto que escribiste' };
  return { tipo: 'lectura', texto: 'Leído de la foto: revisá cada dato contra la factura en papel' };
}

// tipo_iva del POS (tabla tipo_iva): 1 = 10 %, 2 = 5 %, 3 = exento.
const TIPO_IVA_POR_TASA = { 10: 1, 5: 2, 0: 3 };

/**
 * Datos para dar de alta el producto desde una línea de la factura. El código
 * interno del proveedor NO se usa como código propio (otro proveedor usa otro);
 * sí el GTIN o un código impreso con forma de código de barras.
 */
export function altaProductoDesdeLinea(linea) {
  const barcode = [linea?.gtin, linea?.codigo_leido]
    .map((c) => String(c ?? '').trim())
    .find((c) => /^\d{8}$|^\d{12,14}$/.test(c)) || '';
  return {
    codigo: barcode,
    nombre: String(linea?.descripcion_leida ?? '').trim(),
    tipo_iva: TIPO_IVA_POR_TASA[linea?.iva_leido] ?? null
  };
}

const FORMAS = new Set(['SA', 'SRL', 'SAECA', 'SACEI', 'LTDA', 'EIRL', 'CIA', 'Y', 'DE', 'DEL', 'LA', 'EL']);
const normalizarNombre = (nombre) => String(nombre ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/\b([A-Z])\.(?=[A-Z]\.?)/g, '$1')
  .replace(/[^A-Z0-9 ]/g, ' ')
  .split(/\s+/)
  .filter((t) => t && !FORMAS.has(t))
  .join(' ');

/** El nombre de la factura difiere del registrado (sin mayúsculas, puntos ni forma societaria). */
export function nombreProveedorDifiere(leido, registrado) {
  const a = normalizarNombre(leido);
  const b = normalizarNombre(registrado);
  return Boolean(a && b && a !== b);
}

/** Término para abrir el selector de proveedores: la palabra más distintiva del nombre leído. */
export function terminoBusquedaProveedor(nombreLeido) {
  const terminos = normalizarNombre(nombreLeido).split(' ').filter((t) => t.length >= 3);
  return terminos.sort((x, y) => y.length - x.length)[0] || '';
}

/** Normaliza lo que se tipea como número de factura: 1-1-45 → 001-001-0000045. */
export function normalizarNroComprobante(valor) {
  const texto = String(valor ?? '').trim();
  const partes = texto.match(/^(\d{1,3})\D+(\d{1,3})\D+(\d{1,7})$/);
  if (!partes) return texto;
  return `${partes[1].padStart(3, '0')}-${partes[2].padStart(3, '0')}-${partes[3].padStart(7, '0')}`;
}

// ---- detalle del producto y precio de venta (como Facturas Compras y la carga inicial)

/** GET /api/products/:id → lo que se muestra del producto en la línea. */
export function detalleProducto(producto = {}) {
  const porcentaje = numero(producto.porcentaje_iva);
  return {
    codigo: producto.codigo_barras || producto.codigo_producto || null,
    categoria: producto.categoria_nombre || null,
    unidad: producto.unidad_medida_nombre || null,
    porcentaje_iva: porcentaje,
    stock: numero(producto.stock_disponible),
    es_granel: producto.es_granel === true
  };
}

/** Columna de la factura de compra donde cae el subtotal: exenta, iva5 o iva10. */
export function columnaIva(porcentaje) {
  if (porcentaje === 5) return 'iva5';
  if (porcentaje === 0) return 'exenta';
  if (porcentaje === 10) return 'iva10';
  return null;
}

/**
 * GET /api/prices/:producto_id (200, o el cuerpo del 404 «sin precio») → el
 * estado del precio de venta de la línea: el de la carga inicial (precio
 * actual, si se puede editar y por qué no) más el último costo.
 * A granel el precio es por kg: se edita en Precios, no acá.
 */
export function precioDeLinea(info = {}, { sinPrecio = false } = {}) {
  const estado = estadoPrecio(info, { sinPrecio });
  const ultimaCompra = sinPrecio ? null : numero(info.precio_ultima_compra);
  if (estado.esGranel) {
    return { ...estado, celdaHabilitada: false, aviso: 'A granel: el precio por kg se edita en Precios', ultimaCompra: ultimaCompra || null };
  }
  return { ...estado, ultimaCompra: ultimaCompra || null };
}

/** Margen de la línea sobre el costo por unidad de esta factura (venta nueva o, si no hay, la actual). */
export function margenLinea(linea) {
  const venta = numero(linea?.precio_venta_nuevo) || linea?.precio?.precioActual || null;
  return margenSobreCosto(costoPorUnidad(linea?.precio_unitario, linea?.presentacion), venta);
}

/** Variación del costo por unidad contra la última compra, en %; null si no hay con qué comparar. */
export function variacionCosto(linea) {
  const antes = linea?.precio?.ultimaCompra;
  const ahora = costoPorUnidad(linea?.precio_unitario, linea?.presentacion);
  if (!antes || !ahora) return null;
  return Math.round(((ahora - antes) / antes) * 1000) / 10;
}

/** Precios de venta a guardar después de la compra (mismas llamadas que la carga inicial). */
export function planDePreciosCompra(lineas = [], hoy) {
  return planDePrecios(
    lineas
      .filter((l) => l.producto_id && l.precio)
      .map((l) => ({
        producto_id: l.producto_id,
        nombre: l.producto_nombre,
        precio_venta_nuevo: l.precio_venta_nuevo,
        tienePrecio: l.precio.tienePrecio,
        celdaHabilitada: l.precio.celdaHabilitada
      })),
    hoy
  );
}
